"use client";

import { css } from "styled-system/css";
import { useEffect, useRef, useState } from "react";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { es } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { useConfirm } from "@/hooks/useConfirm";
import { ClientSearchAutocomplete, useClientList, type ClientRow } from "./client-search";

export type GoogleEventDrawerData = {
  googleEventId: string | null;
  googleCalendarId: string;
  agendaId: string;
  title: string;
  description: string | null;
  location: string | null;
  htmlLink: string | null;
  status: string | null;
  start: string;
  end: string;
  allDay: boolean;
  calendarColor: string | null;
  calendarName: string | null;
  agendaName: string | null;
  zeroDuration?: boolean;
};

type ServiceOpt = { id: string; name: string; duration: number };

const DEFAULT_TZ = "America/Argentina/Buenos_Aires";

/**
 * Convierte un instante (ISO con offset) al valor de un input datetime-local, pero expresado
 * en la zona del profesional (no la del navegador). Esto evita el desfase de 1h cuando el
 * navegador está en otra zona: lo que se ve y se reenvía coincide con la hora real del evento.
 */
function toLocalDatetimeInput(iso: string, timeZone: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return formatInTimeZone(d, timeZone, "yyyy-MM-dd'T'HH:mm");
}

function extractClientName(title: string): string {
  const first = title.split(/[\s,\-]+/)[0]?.trim() ?? "";
  if (first.length >= 2 && first.length <= 30) {
    return first.charAt(0).toUpperCase() + first.slice(1);
  }
  return title.slice(0, 100);
}

const fieldCss = css({
  w: "100%", px: "3", py: "2", rounded: "lg",
  borderWidth: "1px", borderColor: "#e2e8f0", bg: "white",
  fontSize: "sm",
  _focus: { outline: "none", borderColor: "#6366f1", boxShadow: "0 0 0 3px rgba(99,102,241,0.12)" },
  _disabled: { bg: "#f8fafc", color: "#94a3b8", cursor: "not-allowed" },
});

const labelCss = css({ fontSize: "sm", fontWeight: "medium", color: "#475569" });

export function GoogleEventDrawer({
  open,
  event,
  timeZone,
  onClose,
  onAfterEdit,
}: {
  open: boolean;
  event: GoogleEventDrawerData | null;
  /** Zona horaria IANA del profesional. Las horas se muestran/reenvían en esta zona, no la del navegador. */
  timeZone?: string;
  onClose: () => void;
  onAfterEdit: () => void | Promise<void>;
}) {
  const { refresh } = useAuth();
  const confirm = useConfirm();
  const tz = timeZone ?? DEFAULT_TZ;

  // Edit mode state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [startStr, setStartStr] = useState("");
  const [endStr, setEndStr] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // Convert mode state
  const [convertMode, setConvertMode] = useState(false);
  const [services, setServices] = useState<ServiceOpt[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [convertClientName, setConvertClientName] = useState("");
  const [convertClientEmail, setConvertClientEmail] = useState("");
  const [convertClientPhone, setConvertClientPhone] = useState("");
  const [convertSelectedClientId, setConvertSelectedClientId] = useState<string | null>(null);
  const convertClients = useClientList(convertMode);
  const [convertServiceId, setConvertServiceId] = useState("");
  const [convertNotes, setConvertNotes] = useState("");
  const [convertBusy, setConvertBusy] = useState(false);
  const [convertMsg, setConvertMsg] = useState<string | null>(null);
  const [convertSuccess, setConvertSuccess] = useState(false);
  const convertBodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !event) {
      setMsg(null);
      setConvertMode(false);
      setConvertSuccess(false);
      return;
    }
    setTitle(event.title);
    setDescription(event.description ?? "");
    setLocation(event.location ?? "");
    setStartStr(toLocalDatetimeInput(event.start, tz));
    // zeroDuration: Google no definió un fin real. Dejamos el campo vacío para que
    // el servidor use la duración del servicio seleccionado como fallback.
    setEndStr(event.zeroDuration ? "" : toLocalDatetimeInput(event.end, tz));
    setMsg(null);
    setConvertMode(false);
    setConvertSuccess(false);
  }, [open, event, tz]);

  // Load services when entering convert mode
  useEffect(() => {
    if (!convertMode || services.length > 0) return;
    setServicesLoading(true);
    fetchWithSessionSync("/api/dashboard/services", { credentials: "include" }, refresh)
      .then((r) => r.json())
      .then((data: ServiceOpt[]) => setServices(Array.isArray(data) ? data : []))
      .catch(() => setServices([]))
      .finally(() => setServicesLoading(false));
  }, [convertMode, refresh, services.length]);

  // Pre-fill convert form when entering convert mode
  useEffect(() => {
    if (!convertMode || !event) return;
    setConvertClientName(extractClientName(event.title));
    setConvertClientEmail("");
    setConvertClientPhone("");
    setConvertSelectedClientId(null);
    setConvertServiceId("");
    setConvertNotes("");
    setConvertMsg(null);
    setConvertSuccess(false);
    // Scroll convert body into view
    setTimeout(() => convertBodyRef.current?.scrollTo({ top: 0, behavior: "smooth" }), 50);
  }, [convertMode, event]);

  // Auto-select first service when loaded
  useEffect(() => {
    if (services.length > 0 && !convertServiceId) {
      setConvertServiceId(services[0]!.id);
    }
  }, [services, convertServiceId]);

  if (!open || !event) return null;

  const accentColor = event.calendarColor ?? "#4285F4";
  const canSubmit = title.trim().length > 0 && !busy;

  async function handleSave() {
    if (!event?.googleEventId) return;
    setBusy(true);
    setMsg(null);
    try {
      const body: Record<string, string> = { title: title.trim() };
      body.description = description;
      body.location = location.trim();
      if (!event.allDay && startStr) body.startIso = fromZonedTime(startStr, tz).toISOString();
      if (!event.allDay && endStr) body.endIso = fromZonedTime(endStr, tz).toISOString();
      const r = await fetchWithSessionSync(
        `/api/integrations/google/events/${encodeURIComponent(event.googleCalendarId)}/${encodeURIComponent(event.googleEventId)}?agendaId=${encodeURIComponent(event.agendaId)}`,
        { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
        refresh,
      );
      if (!r.ok) {
        const j = await r.json().catch(() => ({})) as { error?: string };
        setMsg(j.error ?? "No se pudo guardar");
        return;
      }
      await onAfterEdit();
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!event?.googleEventId) return;
    const ok = await confirm({ message: "¿Eliminar este evento de Google Calendar?", tone: "danger", confirmLabel: "Eliminar" });
    if (!ok) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await fetchWithSessionSync(
        `/api/integrations/google/events/${encodeURIComponent(event.googleCalendarId)}/${encodeURIComponent(event.googleEventId)}?agendaId=${encodeURIComponent(event.agendaId)}`,
        { method: "DELETE" },
        refresh,
      );
      if (!r.ok) {
        const j = await r.json().catch(() => ({})) as { error?: string };
        setMsg(j.error ?? "No se pudo eliminar");
        return;
      }
      await onAfterEdit();
    } finally {
      setBusy(false);
    }
  }

  function selectConvertClient(c: ClientRow) {
    setConvertSelectedClientId(c.clientId);
    setConvertClientName(c.displayName);
    setConvertClientEmail(c.email ?? "");
    setConvertClientPhone(c.phone ?? "");
    setConvertMsg(null);
  }

  function clearConvertClientSelection() {
    setConvertSelectedClientId(null);
    setConvertClientName("");
    setConvertClientEmail("");
    setConvertClientPhone("");
  }

  async function handleConvert() {
    if (!event?.googleEventId) return;

    if (!convertClientEmail.trim() && !convertClientPhone.trim()) {
      setConvertMsg("Ingresá al menos un email o número de teléfono para poder enviar las notificaciones.");
      return;
    }
    if (!convertServiceId) {
      setConvertMsg("Seleccioná un servicio.");
      return;
    }
    if (!startStr) {
      setConvertMsg("Se requiere fecha y hora de inicio.");
      return;
    }

    setConvertBusy(true);
    setConvertMsg(null);
    try {
      const body: Record<string, unknown> = {
        clientName: convertClientName.trim(),
        clientEmail: convertClientEmail.trim() || undefined,
        clientPhone: convertClientPhone.trim() || null,
        serviceId: convertServiceId,
        startIso: fromZonedTime(startStr, tz).toISOString(),
        endIso: endStr ? fromZonedTime(endStr, tz).toISOString() : undefined,
        notes: convertNotes.trim() || null,
      };
      const r = await fetchWithSessionSync(
        `/api/integrations/google/events/${encodeURIComponent(event.googleCalendarId)}/${encodeURIComponent(event.googleEventId)}/convert-to-appointment?agendaId=${encodeURIComponent(event.agendaId)}`,
        { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
        refresh,
      );
      if (!r.ok) {
        const j = await r.json().catch(() => ({})) as { error?: string };
        setConvertMsg(j.error ?? "No se pudo crear el turno");
        return;
      }
      setConvertSuccess(true);
      setTimeout(() => {
        void onAfterEdit();
      }, 1200);
    } finally {
      setConvertBusy(false);
    }
  }

  return (
    <div
      className={css({ position: "fixed", inset: 0, zIndex: 60, bg: "rgba(0,0,0,0.35)", display: "flex", justifyContent: "flex-end" })}
      onClick={(e) => e.currentTarget === e.target && onClose()}
    >
      <aside
        className={css({
          width: { base: "100%", sm: "420px" },
          height: "100%",
          maxHeight: "100dvh",
          bg: "white",
          borderLeftWidth: "1px",
          borderColor: "#e2e8f0",
          display: "flex",
          flexDir: "column",
          overflow: "hidden",
        })}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={css({
            flexShrink: 0, px: "5", pt: "5", pb: "4",
            borderBottomWidth: "1px", borderColor: "#f1f5f9",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: "3",
          })}
        >
          <div className={css({ display: "flex", gap: "3", alignItems: "center", minW: 0 })}>
            {convertMode ? (
              <button
                type="button"
                onClick={() => setConvertMode(false)}
                disabled={convertBusy}
                className={css({
                  w: "9", h: "9", rounded: "lg", display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, bg: "#f1f5f9", border: "none", cursor: "pointer", color: "#475569",
                  _hover: { bg: "#e2e8f0" }, _disabled: { opacity: 0.5 },
                })}
                aria-label="Volver"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
              </button>
            ) : (
              <div
                className={css({ w: "9", h: "9", rounded: "lg", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 })}
                style={{ backgroundColor: `${accentColor}22`, border: `1.5px solid ${accentColor}55` }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
              </div>
            )}
            <div className={css({ minW: 0 })}>
              {convertMode ? (
                <p className={css({ fontWeight: "bold", color: "#0f172a", fontSize: "sm" })}>
                  Convertir en turno
                </p>
              ) : (
                <>
                  <div className={css({ display: "flex", alignItems: "center", gap: "1.5" })}>
                    <span
                      className={css({ w: "2.5", h: "2.5", rounded: "full", flexShrink: 0 })}
                      style={{ backgroundColor: accentColor }}
                    />
                    <p className={css({ fontWeight: "bold", color: "#0f172a", fontSize: "sm", truncate: true })}>
                      {event.calendarName ?? "Evento de Google Calendar"}
                    </p>
                  </div>
                  {event.agendaName ? (
                    <span className={css({ fontSize: "xs", color: "#64748b", pl: "4" })}>{event.agendaName}</span>
                  ) : null}
                </>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={css({ border: "none", bg: "transparent", cursor: "pointer", fontSize: "20px", color: "#64748b", flexShrink: 0 })}
            aria-label="Cerrar"
          >
            x
          </button>
        </div>

        {/* Body */}
        <div ref={convertBodyRef} className={css({ flex: "1", minH: "0", overflowY: "auto", px: "5", py: "4" })}>
          {convertMode ? (
            /* Convert form */
            <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
              {convertSuccess ? (
                <div className={css({
                  display: "flex", flexDir: "column", alignItems: "center", justifyContent: "center",
                  gap: "3", py: "10", textAlign: "center",
                })}>
                  <div className={css({
                    w: "14", h: "14", rounded: "full", bg: "#dcfce7",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  })}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </div>
                  <div>
                    <p className={css({ fontWeight: "semibold", color: "#0f172a", fontSize: "base" })}>Turno creado</p>
                    <p className={css({ fontSize: "sm", color: "#64748b", mt: "1" })}>
                      Se enviaron las notificaciones al cliente.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {convertMsg ? (
                    <p className={css({ fontSize: "sm", color: "#dc2626", rounded: "lg", px: "3", py: "2", bg: "#fef2f2", borderWidth: "1px", borderColor: "#fecaca" })}>
                      {convertMsg}
                    </p>
                  ) : null}

                  <div className={css({ rounded: "xl", bg: "#f8fafc", borderWidth: "1px", borderColor: "#e2e8f0", px: "3", py: "2.5" })}>
                    <p className={css({ fontSize: "xs", fontWeight: "semibold", color: "#64748b", mb: "0.5" })}>Evento</p>
                    <p className={css({ fontSize: "sm", fontWeight: "medium", color: "#0f172a" })}>{event.title}</p>
                    {!event.allDay && startStr ? (
                      <p className={css({ fontSize: "xs", color: "#64748b", mt: "0.5" })}>
                        {new Date(startStr).toLocaleString("es-AR", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </p>
                    ) : null}
                  </div>

                  <ClientSearchAutocomplete
                    clients={convertClients}
                    onSelect={selectConvertClient}
                    disabled={convertBusy}
                  />

                  {convertSelectedClientId && (
                    <div
                      className={css({
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "3",
                        px: "3",
                        py: "1.5",
                        bg: "#eef2ff",
                        rounded: "lg",
                        borderWidth: "1px",
                        borderColor: "#c7d2fe",
                      })}
                    >
                      <span className={css({ fontSize: "xs", color: "#4338ca" })}>
                        Cliente del CRM seleccionado
                      </span>
                      <button
                        type="button"
                        onClick={clearConvertClientSelection}
                        disabled={convertBusy}
                        className={css({ fontSize: "xs", color: "#6366f1", cursor: "pointer", flexShrink: 0, border: "none", bg: "transparent", _hover: { color: "#4338ca" } })}
                      >
                        Cambiar
                      </button>
                    </div>
                  )}

                  <div className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                    <label className={labelCss}>Nombre del cliente <span className={css({ color: "#dc2626" })}>*</span></label>
                    <input
                      type="text"
                      value={convertClientName}
                      onChange={(e) => setConvertClientName(e.target.value)}
                      disabled={convertBusy}
                      placeholder="Nombre del cliente"
                      className={fieldCss}
                    />
                  </div>

                  <div className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                    <label className={labelCss}>Email</label>
                    <input
                      type="email"
                      value={convertClientEmail}
                      onChange={(e) => setConvertClientEmail(e.target.value)}
                      disabled={convertBusy}
                      placeholder="email@ejemplo.com"
                      className={fieldCss}
                    />
                  </div>

                  <div className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                    <label className={labelCss}>Teléfono</label>
                    <input
                      type="tel"
                      value={convertClientPhone}
                      onChange={(e) => setConvertClientPhone(e.target.value)}
                      disabled={convertBusy}
                      placeholder="+549..."
                      className={fieldCss}
                    />
                  </div>

                  <p className={css({ fontSize: "xs", color: "#94a3b8", mt: "-2" })}>
                    Se requiere al menos email o teléfono para enviar las notificaciones.
                  </p>

                  <div className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                    <label className={labelCss}>Servicio <span className={css({ color: "#dc2626" })}>*</span></label>
                    {servicesLoading ? (
                      <p className={css({ fontSize: "sm", color: "#94a3b8" })}>Cargando servicios...</p>
                    ) : services.length === 0 ? (
                      <p className={css({ fontSize: "sm", color: "#f59e0b", rounded: "lg", px: "3", py: "2", bg: "#fffbeb", borderWidth: "1px", borderColor: "#fcd34d" })}>
                        No tenés servicios activos. Creá uno en Configuracion antes de continuar.
                      </p>
                    ) : (
                      <select
                        value={convertServiceId}
                        onChange={(e) => setConvertServiceId(e.target.value)}
                        disabled={convertBusy}
                        className={fieldCss}
                      >
                        {services.map((s) => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  {!event.allDay ? (
                    <>
                      <div className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                        <label className={labelCss}>Inicio</label>
                        <input
                          type="datetime-local"
                          value={startStr}
                          onChange={(e) => setStartStr(e.target.value)}
                          disabled={convertBusy}
                          className={fieldCss}
                        />
                      </div>
                      <div className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                        <label className={labelCss}>Fin</label>
                        <input
                          type="datetime-local"
                          value={endStr}
                          onChange={(e) => setEndStr(e.target.value)}
                          disabled={convertBusy}
                          className={fieldCss}
                        />
                      </div>
                    </>
                  ) : null}

                  <div className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                    <label className={labelCss}>Notas internas</label>
                    <textarea
                      value={convertNotes}
                      onChange={(e) => setConvertNotes(e.target.value)}
                      disabled={convertBusy}
                      rows={2}
                      placeholder="Opcional"
                      className={fieldCss}
                    />
                  </div>
                </>
              )}
            </div>
          ) : (
            /* Edit form (original) */
            <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
              {msg ? (
                <p className={css({ fontSize: "sm", color: "#dc2626", rounded: "lg", px: "3", py: "2", bg: "#fef2f2", borderWidth: "1px", borderColor: "#fecaca" })}>
                  {msg}
                </p>
              ) : null}

              <div className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                <label className={labelCss}>Titulo</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={busy}
                  className={fieldCss}
                />
              </div>

              {event.status === "tentative" ? (
                <span
                  className={css({ alignSelf: "flex-start", fontSize: "xs", fontWeight: "semibold", px: "2", py: "0.5", rounded: "full", bg: "#fffbeb", color: "#b45309", borderWidth: "1px", borderColor: "#fcd34d" })}
                >
                  Provisional
                </span>
              ) : null}

              {!event.allDay ? (
                <>
                  <div className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                    <label className={labelCss}>Inicio</label>
                    <input
                      type="datetime-local"
                      value={startStr}
                      onChange={(e) => setStartStr(e.target.value)}
                      disabled={busy}
                      className={fieldCss}
                    />
                  </div>
                  <div className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                    <label className={labelCss}>Fin</label>
                    <input
                      type="datetime-local"
                      value={endStr}
                      onChange={(e) => setEndStr(e.target.value)}
                      disabled={busy}
                      className={fieldCss}
                    />
                  </div>
                </>
              ) : (
                <p className={css({ fontSize: "sm", color: "#64748b" })}>
                  Evento de todo el dia - {formatInTimeZone(new Date(event.start), tz, "EEEE d 'de' MMMM", { locale: es })}
                </p>
              )}

              <div className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                <label className={labelCss}>Descripcion</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={busy}
                  rows={3}
                  placeholder="Sin descripcion"
                  className={fieldCss}
                />
              </div>

              <div className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                <label className={labelCss}>Ubicacion</label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  disabled={busy}
                  placeholder="Sin ubicacion"
                  className={fieldCss}
                />
              </div>

              {event.htmlLink ? (
                <a
                  href={event.htmlLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css({
                    display: "inline-flex", alignItems: "center", gap: "1.5",
                    fontSize: "sm", color: "#4285F4", fontWeight: "medium",
                    _hover: { textDecoration: "underline" },
                  })}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M19 19H5V5h7V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>
                  </svg>
                  Abrir en Google Calendar
                </a>
              ) : null}

              {!event.googleEventId ? (
                <p className={css({ fontSize: "xs", color: "#94a3b8" })}>Este evento no tiene ID de Google y no puede editarse.</p>
              ) : null}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={css({
            flexShrink: 0, px: "5", pb: "5", pt: "4",
            borderTopWidth: "1px", borderColor: "#f1f5f9", bg: "#fafafa",
            display: "flex", flexDir: "column", gap: "2",
          })}
        >
          {convertMode ? (
            <>
              <Button
                variant="primary"
                disabled={convertBusy || convertSuccess || services.length === 0 || servicesLoading}
                onClick={() => void handleConvert()}
                className={css({ bg: "linear-gradient(90deg, #16a34a, #22c55e) !important" })}
              >
                {convertBusy ? "Creando turno..." : convertSuccess ? "Turno creado" : "Confirmar turno"}
              </Button>
              <Button
                variant="secondary"
                onClick={() => setConvertMode(false)}
                disabled={convertBusy || convertSuccess}
              >
                Cancelar
              </Button>
            </>
          ) : (
            <>
              <button
                type="button"
                disabled={busy || !event.googleEventId}
                onClick={() => setConvertMode(true)}
                className={css({
                  w: "100%", px: "4", py: "2.5", rounded: "lg",
                  fontSize: "sm", fontWeight: "semibold", cursor: "pointer",
                  border: "1.5px solid #16a34a",
                  bg: "#f0fdf4", color: "#15803d",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "2",
                  transition: "transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
                  _hover: { bg: "#dcfce7" },
                  _active: { transform: "scale(0.97)" },
                  _disabled: { opacity: 0.4, cursor: "not-allowed" },
                })}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                  <path d="M12 11v6M9 14h6"/>
                </svg>
                Hacer turno
              </button>
              <div className={css({ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2" })}>
                <Button
                  variant="primary"
                  disabled={!canSubmit || !event.googleEventId}
                  onClick={() => void handleSave()}
                >
                  {busy ? "Guardando..." : "Guardar"}
                </Button>
                <Button variant="secondary" onClick={onClose} disabled={busy}>
                  Cancelar
                </Button>
              </div>
              <Button
                variant="secondary"
                disabled={busy || !event.googleEventId}
                onClick={() => void handleDelete()}
                className={css({ color: "#dc2626 !important", borderColor: "#fecaca !important", _hover: { bg: "#fef2f2 !important" } })}
              >
                {busy ? "..." : "Eliminar evento"}
              </Button>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
